<!--
This component shows a string input field (to determine a GEO point) and a dropdown (to determine the distance to search around that point).
The string input can be either a zipcode or part of a city name
-->

<template>
  <div class="flex">
    <FormKit
      :value="location"
      :debounce="300"
      :title="t_('placeholder')"
      type="search"
      :help="city ? t_('found', { city }) : undefined"
      :placeholder="t_('placeholder')"
      :errors="errors.map((error) => t_(error))"
      input-class="c-location-search-input"
      class="grow"
      @input="(value) => $emit('location', value)"
    />
    <FormKit
      :value="distance"
      :label="t_('distance')"
      type="select"
      input-class="c-small-chevron c-location-search-input"
      :options="distanceOptions"
      @input="(value) => $emit('distance', value)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { distances, fallbackDistance } from '~/store/location-search'
import { setupTranslationPrefixer } from '~/composables/util'

interface Props {
  city?: string
  distance?: string
  errors?: string[]
  location?: string
  routeQueryName?: string
}

const props = withDefaults(defineProps<Props>(), {
  city: '',
  distance: fallbackDistance,
  errors: () => [],
  location: '',
  routeQueryName: undefined,
})
defineEmits(['distance', 'location'])

const { t_ } = setupTranslationPrefixer('LocationSearchInput')

const distance = ref(props.distance || fallbackDistance)
const location = ref(props.location || '')
const distanceOptions = computed(() => distances.map((distance) => ({ value: distance, label: t_(distance) })))
</script>

<style lang="scss" scoped>
:deep(.formkit-label) {
  @apply text-black sr-only;
}

:deep(.formkit-wrapper) {
  @apply mb-1;

  .formkit-input[type='search'] {
    z-index: 1;
  }

  .formkit-inner:has(.formkit-input[type='search'].c-location-search-input) {
    @apply rounded-r-none text-base;
    height: 46px;
  }

  .formkit-inner:has(.formkit-input[type='search'].c-location-search-input:focus) {
    @apply rounded-r;
  }

  .formkit-inner:has(select.formkit-input:not([multiple]).c-location-search-input) {
    @apply rounded-r rounded-l-none text-xs mr-3;
  }
  .formkit-inner:has(select.formkit-input:not([multiple]).c-location-search-input:focus) {
    @apply rounded-l;
    box-shadow: 0 0 0 1px theme('colors.app.green');
    outline: theme('colors.app.green') auto 1px;
  }
  .formkit-inner select.formkit-input:not([multiple]).c-location-search-input {
    line-height: 21px;
    height: 46px;
  }
}
</style>
